import { createRef, Component } from 'react';
import { FormattedMessage } from '@alltrails/shared/react-intl';
import { getUserProfilePhoto } from '@alltrails/shared/utils/requests/photoRequests';
import { PhotoSize } from '@alltrails/shared/types/photos';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import PhotoTimeStamp from './PhotoTimeStamp';
import { PhotoCaption } from '../../../application/javascripts/react_components/photos/photo_caption';
import {
  deletePhoto,
  suggestDeletePhotoFromTrail,
  unhidePhoto,
  removePhotoFromTrail,
  removePhotoFromMap,
  setAsMapProfilePhoto,
  setAsListProfilePhoto,
  setAsTrailProfilePhoto
} from '../../../utils/requests/photo_requests';
import * as styles from './styles/styles.module.scss';

class PhotoCreditLink extends Component {
  constructor(props) {
    super(props);

    this.firstLink = createRef();
    this.secondLink = createRef();
    this.unHideLink = createRef();
  }

  static LINK_DELETE_PHOTO_COMPLETELY = 1;

  static LINK_REMOVE_PHOTO_FROM_MAP = 2;

  static LINK_SUGGEST_DELETE_FROM_TRAIL = 3;

  static LINK_SET_AS_DEFAULT_MAP_PHOTO = 4;

  static LINK_SET_AS_DEFAULT_TRAIL_PHOTO = 5;

  // Currently unused
  // static LINK_SUGGEST_AS_DEFAULT_TRAIL_PHOTO = 6;

  static LINK_SET_AS_DEFAULT_LIST_PHOTO = 7;

  static LINK_REMOVE_PHOTO_FROM_TRAIL = 8;

  // handlers
  handleAdminLinkClick = (e, linkRef, actionEnum) => {
    e.preventDefault();

    const a = linkRef.current;
    const {
      includeListId: listId,
      trailSlug,
      includeMapId: mapId,
      photo: { id: photoId }
    } = this.props;

    switch (actionEnum) {
      case PhotoCreditLink.LINK_DELETE_PHOTO_COMPLETELY:
        if (!window.confirm('Are you sure you want to delete this photo?')) {
          return null;
        }
        a.innerHMTL = <FormattedMessage defaultMessage="Deleted" />;
        return deletePhoto(photoId).then(() => {
          a.innerHTML = <FormattedMessage defaultMessage="Deleted" />;
          window.location.reload();
        });
      case PhotoCreditLink.LINK_REMOVE_PHOTO_FROM_MAP:
        a.innerHMTL = <FormattedMessage defaultMessage="Removing..." />;
        return removePhotoFromMap(mapId, photoId).then(() => {
          a.innerHTML = <FormattedMessage defaultMessage="Removed" />;
          window.location.reload();
        });
      case PhotoCreditLink.LINK_SUGGEST_DELETE_FROM_TRAIL:
        a.innerHTML = <FormattedMessage defaultMessage="Suggesting..." />;
        return suggestDeletePhotoFromTrail(trailSlug, photoId).then(() => {
          a.innerHTML = <FormattedMessage defaultMessage="Thank you" />;
        });
      case PhotoCreditLink.LINK_SET_AS_DEFAULT_MAP_PHOTO:
        a.innerHMTL = <FormattedMessage defaultMessage="Setting..." />;
        return setAsMapProfilePhoto(mapId, photoId).then(() => {
          a.innerHTML = <FormattedMessage defaultMessage="Set" />;
          this.postalCallback('map.default_photo.change', { map_id: mapId, photo_id: photoId });
        });
      case PhotoCreditLink.LINK_SET_AS_DEFAULT_TRAIL_PHOTO:
        a.innerHTML = <FormattedMessage defaultMessage="Setting..." />;
        return setAsTrailProfilePhoto(trailSlug, photoId).then(() => {
          a.innerHTML = <FormattedMessage defaultMessage="Set" />;
          window.location.reload();
        });
      case PhotoCreditLink.LINK_SET_AS_DEFAULT_LIST_PHOTO:
        a.innerHMTL = <FormattedMessage defaultMessage="Setting..." />;
        return setAsListProfilePhoto(listId, photoId).then(data => {
          a.innerHTML = <FormattedMessage defaultMessage="Set" />;
          const cssBackgroundUrl = `url("${data.url}")`;
          $('#list-profile-photo').css({
            'background-image': `linear-gradient(rgba(0, 0, 0, 0.0), rgba(0, 0, 0, 0.6)), ${cssBackgroundUrl}`,
            'background-repeat': 'no-repeat',
            'background-position': 'center center'
          });
        });
      case PhotoCreditLink.LINK_REMOVE_PHOTO_FROM_TRAIL:
        a.innerHMTL = <FormattedMessage defaultMessage="Removing..." />;
        return removePhotoFromTrail(trailSlug, photoId).then(() => {
          a.innerHTML = <FormattedMessage defaultMessage="Removed" />;
          window.location.reload();
        });
      default:
        return null;
    }
  };

  postalCallback = (name, data) => {
    this.messagingChannel.publish(name, data);
  };

  getPhotoCaptionProps() {
    return {
      photoId: this.props.photo.id,
      title: this.props.creditTitle.title,
      titleUrl: this.props.photoTrailUrl || this.props.creditTitle.title_url,
      canEdit: this.props.context.current_user && this.props.photo.user_id === this.props.context.current_user.user.id
    };
  }

  renderFirstAdminLink() {
    return (
      <a
        ref={this.firstLink}
        className={styles.adminLink}
        href={this.props.firstLink.href}
        onClick={e => this.handleAdminLinkClick(e, this.firstLink, this.props.firstLink.actionEnum)}
      >
        {this.props.firstLink.label}
      </a>
    );
  }

  renderSecondAdminLink() {
    return (
      <a
        ref={this.secondLink}
        className={styles.adminLink}
        href={this.props.secondLink.href}
        onClick={e => this.handleAdminLinkClick(e, this.secondLink, this.props.secondLink.actionEnum)}
      >
        {this.props.secondLink.label}
      </a>
    );
  }

  renderTimeStamp() {
    if (!this.props.timestamp) {
      return null;
    }

    return <PhotoTimeStamp timestamp={this.props.timestamp} />;
  }

  renderUserImage() {
    const photoCaptionProps = this.getPhotoCaptionProps();

    return (
      <div className={styles.photoInfoContainer}>
        <a href={this.props.creditTitle.user_profile_url}>
          <img alt="profile-image" width={34} height={34} src={getUserProfilePhoto(this.props.photo.user_id, PhotoSize.SmallSquare)} />
        </a>
        <div>
          <div className={styles.userAndCaption}>
            <div className={styles.userAndTimestamp}>
              <a className={classNames('xlate-none', styles.userLink)} href={this.props.creditTitle.user_profile_url} target="_blank">
                {this.props.creditTitle.user_name}
              </a>
              {this.renderTimeStamp()}
            </div>
            <span className="captionContainer">
              <PhotoCaption {...photoCaptionProps} />
            </span>
          </div>
        </div>
      </div>
    );
  }

  handleUnhideClick = e => {
    e.preventDefault();

    return unhidePhoto(this.props.photo.id).then(() => {
      this.unHideLink.current.innerHTML = 'Unhidden';
    });
  };

  renderPhotoUnhide() {
    if (!this.props.photoUnhideLink) {
      return null;
    }

    return (
      <a ref={this.unHideLink} className={styles.unhideLink} href={this.props.photoUnhideLink.href} onClick={this.handleUnhideClick}>
        {this.props.photoUnhideLink.label}
      </a>
    );
  }

  renderAdminLinks() {
    let adminLinks = <></>;
    if (this.props.firstLink && this.props.secondLink) {
      adminLinks = (
        <>
          {this.renderFirstAdminLink()} | {this.renderSecondAdminLink()}
        </>
      );
    } else if (this.props.firstLink) {
      adminLinks = this.renderFirstAdminLink();
    } else if (this.props.secondLink) {
      adminLinks = this.renderSecondAdminLink();
    }

    return adminLinks;
  }

  renderAdminControls() {
    return (
      <div>
        {this.renderPhotoUnhide()}
        {this.renderAdminLinks()}
      </div>
    );
  }

  render() {
    return (
      <div className={styles.creditLinkContainer}>
        {this.renderUserImage()}
        {this.renderAdminControls()}
      </div>
    );
  }
}

PhotoCreditLink.propTypes = {
  context: PropTypes.shape({
    locale: PropTypes.string,
    currentUser: PropTypes.shape({
      user: PropTypes.shape({
        id: PropTypes.number
      }),
      isAdmin: PropTypes.bool
    }),
    creditTitle: PropTypes.shape({
      title: PropTypes.string,
      user_name: PropTypes.string,
      user_profile_url: PropTypes.stringj
    }),
    firstLink: PropTypes.shape({
      href: PropTypes.string,
      actionEnum: PropTypes.number,
      label: PropTypes.string
    }),
    secondLink: PropTypes.shape({
      href: PropTypes.string,
      actionEnum: PropTypes.number,
      label: PropTypes.string
    }),
    includeListId: PropTypes.number,
    photoTrailUrl: PropTypes.string,
    photoUnhideLink: PropTypes.shape({
      href: PropTypes.string,
      actionEnum: PropTypes.number,
      label: PropTypes.string
    })
  })
};

export default PhotoCreditLink;
